import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ClearIcon from '@mui/icons-material/Clear'
import GroupsIcon from '@mui/icons-material/Groups'
import InboxIcon from '@mui/icons-material/Inbox'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonIcon from '@mui/icons-material/Person'
import SearchIcon from '@mui/icons-material/Search'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'

import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import FilterPills from '../../../components/Filters/FilterPills'
import ApplicationReviewDrawer from './ApplicationReviewDrawer'
import { getApplicantLabel, groupShiftApplications } from './groupApplications'
import {
  SHIFT_TYPE_CHIP_COLOURS,
  SHIFT_TYPE_FILTERS,
  getShiftListType,
} from '../../../helpers/shiftListType'

const getApplicationTitle = (application) =>
  application?.shift?.event?.title ??
  application?.shift?.shiftType ??
  'Shift application'

const Applications = () => {
  const [applications, setApplications] = useState([])
  const [total, setTotal] = useState(0)
  const [typeCounts, setTypeCounts] = useState(null)
  const [eventOptions, setEventOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [typeFilter, setTypeFilter] = useState('All')
  const [eventFilter, setEventFilter] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searchPlaceholder, setSearchPlaceholder] = useState(
    'Search applications...',
  )

  const [selectedGroup, setSelectedGroup] = useState(null)

  const theme = useTheme()
  const isMobile = useIsMobile()

  // Returning the same model object when the page is already 0 lets React
  // bail out of the state update, so this can't trigger a redundant refetch
  // (the fetch effect depends on the model's identity).
  const resetToFirstPage = () =>
    setPaginationModel((model) =>
      model.page === 0 ? model : { ...model, page: 0 },
    )

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
      resetToFirstPage()
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadApplications = () => {
    setLoading(true)
    setFetchError(false)

    const params = new URLSearchParams({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    })
    if (typeFilter !== 'All') params.set('shiftType', typeFilter)
    if (typeFilter === 'Event' && eventFilter?._id) {
      params.set('event', eventFilter._id)
    }
    if (debouncedSearch) params.set('search', debouncedSearch)

    API.get(`/shift-applications?${params.toString()}`)
      .then((res) => {
        if (res.status === 200) {
          setApplications(res.data?.applications ?? [])
          setTotal(res.data?.total ?? 0)
          setTypeCounts(res.data?.typeCounts ?? null)
          setEventOptions(res.data?.eventOptions ?? [])
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadApplications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, typeFilter, eventFilter, debouncedSearch])

  // Reviewing the last rows on a page can leave the grid pointing past the
  // end of the (now smaller) result set - snap back to the last real page.
  useEffect(() => {
    if (loading) return
    const lastPage = Math.max(Math.ceil(total / paginationModel.pageSize) - 1, 0)
    if (total > 0 && paginationModel.page > lastPage) {
      setPaginationModel((model) => ({ ...model, page: lastPage }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, total])

  // The server paginates per review row (group) and returns every
  // application belonging to the current page's groups - regrouping here
  // reproduces those rows exactly.
  const groupedApplications = useMemo(
    () => groupShiftApplications(applications),
    [applications],
  )

  const hasActiveFilters =
    typeFilter !== 'All' || !!eventFilter || !!debouncedSearch
  const noPendingAtAll =
    !loading && !fetchError && total === 0 && !hasActiveFilters

  const renderTypeChip = (row) => {
    const type = getShiftListType(row.shift)
    const colour =
      theme.palette[SHIFT_TYPE_CHIP_COLOURS[type]] ?? theme.palette.anBlue
    return (
      <Chip
        size="small"
        label={type === 'Event' ? 'Event' : type}
        sx={{
          borderRadius: 1,
          fontWeight: 700,
          bgcolor: alpha(colour.main, 0.1),
          color: colour.main,
        }}
      />
    )
  }

  const renderApplicantAvatar = (row) => {
    const representative = row.applications[0]
    const isMultiple = row.applicantCount > 1
    return (
      <Avatar
        src={
          !isMultiple && !row.alreadyFilled
            ? representative?.employee?.userAccount?.photograph
            : undefined
        }
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1,
          flexShrink: 0,
          bgcolor: row.alreadyFilled
            ? alpha(theme.palette.warning.main, 0.12)
            : alpha(theme.palette.anBlue.main, 0.1),
          color: row.alreadyFilled ? 'warning.main' : 'anBlue.main',
        }}
      >
        {row.alreadyFilled ? (
          <WarningAmberIcon fontSize="small" />
        ) : isMultiple ? (
          <GroupsIcon fontSize="small" />
        ) : (
          <PersonIcon fontSize="small" />
        )}
      </Avatar>
    )
  }

  const columns = [
    {
      field: 'applicant',
      headerName: 'Applicant',
      minWidth: 220,
      flex: 1.2,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" gap={1.25} alignItems="center" minWidth={0}>
          {renderApplicantAvatar(row)}
          <Typography fontWeight={700} noWrap>
            {getApplicantLabel(row)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'shift',
      headerName: 'Shift',
      minWidth: 260,
      flex: 1.6,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
          <Typography fontWeight={700} noWrap>
            {getApplicationTitle(row.applications[0])}
          </Typography>
          {row.shift?.clinicalRole && (
            <Chip
              size="small"
              label={row.shift.clinicalRole}
              sx={{ borderRadius: 1 }}
            />
          )}
          {row.alreadyFilled && (
            <Chip
              size="small"
              color="warning"
              icon={<WarningAmberIcon />}
              label="Already Filled"
              sx={{ borderRadius: 1 }}
            />
          )}
        </Stack>
      ),
    },
    {
      field: 'date',
      headerName: 'Date',
      minWidth: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          icon={<CalendarMonthIcon />}
          label={moment(row.shift.start).format('ddd Do MMM')}
          sx={{ borderRadius: 1 }}
        />
      ),
    },
    {
      field: 'time',
      headerName: 'Time',
      minWidth: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          icon={<AccessTimeIcon />}
          label={`${moment(row.shift.start).format('HH:mm')}-${moment(
            row.shift.end,
          ).format('HH:mm')}`}
          sx={{ borderRadius: 1 }}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => renderTypeChip(row),
    },
    {
      field: 'open',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: () => (
        <IconButton aria-label="Open shift application" color="anBlue">
          <ArrowForwardIcon />
        </IconButton>
      ),
    },
  ]

  // Phones: one stacked "card" column per row instead of six narrow columns
  // that would force the grid to scroll sideways.
  const mobileColumns = [
    {
      field: 'summary',
      headerName: 'Applications',
      flex: 1,
      minWidth: 0,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          gap={1.25}
          alignItems="center"
          minWidth={0}
          width="100%"
        >
          {renderApplicantAvatar(row)}
          <Stack minWidth={0} flex={1} gap={0.25}>
            <Stack direction="row" gap={1} alignItems="baseline" minWidth={0}>
              <Typography fontWeight={700} noWrap lineHeight={1.3} flex={1}>
                {getApplicantLabel(row)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                flexShrink={0}
              >
                {moment(row.shift.start).format('ddd D MMM')}
              </Typography>
            </Stack>
            <Typography variant="body2" noWrap lineHeight={1.3}>
              {getApplicationTitle(row.applications[0])}
            </Typography>
            <Stack direction="row" gap={0.5} alignItems="center" minWidth={0}>
              {renderTypeChip(row)}
              {row.shift?.clinicalRole && (
                <Chip
                  size="small"
                  label={row.shift.clinicalRole}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {row.alreadyFilled && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<WarningAmberIcon />}
                  label="Filled"
                  sx={{ borderRadius: 1 }}
                />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ ml: 0.5 }}
              >
                {moment(row.shift.start).format('HH:mm')}-
                {moment(row.shift.end).format('HH:mm')}
              </Typography>
            </Stack>
          </Stack>
          <ArrowForwardIcon color="anBlue" fontSize="small" />
        </Stack>
      ),
    },
  ]

  return (
    <Stack gap={2} minWidth={0} sx={{ maxWidth: '100%' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={1.5}
        minWidth={0}
      >
        <Stack gap={0.5}>
          <Stack direction="row" gap={1} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.anBlue.main, 0.1),
                color: 'anBlue.main',
              }}
            >
              <PersonAddIcon />
            </Box>
            <Typography variant="title2">Shift Applications</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? 'Loading applications'
              : `${total} pending ${total === 1 ? 'application' : 'applications'}${
                  hasActiveFilters ? ' matching your filters' : ''
                }`}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            width: { xs: '100%', md: 360 },
            boxSizing: 'border-box',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: alpha(theme.palette.anBlue.main, 0.22),
            bgcolor: 'background.paper',
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <TextField
            variant="standard"
            placeholder={searchPlaceholder}
            fullWidth
            value={searchInput}
            onFocus={() => setSearchPlaceholder('Name, role, or event')}
            onBlur={() => setSearchPlaceholder('Search applications...')}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{ disableUnderline: true }}
            autoComplete="off"
          />
          {searchInput && (
            <IconButton size="small" onClick={() => setSearchInput('')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <FilterPills
          ariaLabel="Shift type"
          value={typeFilter}
          options={SHIFT_TYPE_FILTERS}
          counts={typeCounts}
          onChange={(value) => {
            setTypeFilter(value)
            if (value !== 'Event') setEventFilter(null)
            resetToFirstPage()
          }}
        />

        {typeFilter === 'Event' && (
          <Autocomplete
            size="small"
            options={eventOptions}
            value={eventFilter}
            onChange={(e, value) => {
              setEventFilter(value)
              resetToFirstPage()
            }}
            getOptionLabel={(option) => option?.title ?? ''}
            isOptionEqualToValue={(option, value) =>
              String(option?._id) === String(value?._id)
            }
            sx={{ width: { xs: '100%', sm: 280 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by event"
                placeholder="All events"
              />
            )}
          />
        )}
      </Stack>

      {fetchError && (
        <Alert severity="error">Unable to load shift applications.</Alert>
      )}

      {noPendingAtAll && (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.04),
            borderColor: alpha(theme.palette.success.main, 0.22),
          }}
        >
          <Stack gap={1} alignItems="center">
            <InboxIcon color="success" />
            <Typography fontWeight={800}>No pending applications</Typography>
            <Typography variant="body2" color="text.secondary">
              All caught up.
            </Typography>
          </Stack>
        </Paper>
      )}

      {!fetchError && !noPendingAtAll && (
        <Paper sx={{ borderRadius: 2 }}>
          <CustomDataGrid
            loading={loading}
            rows={groupedApplications}
            columns={isMobile ? mobileColumns : columns}
            getRowId={(row) => row.key}
            rowHeight={isMobile ? 92 : 64}
            onRowClick={({ row }) => setSelectedGroup(row)}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            hideToolbar
            pagination
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            slots={{
              noRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                  gap={0.5}
                >
                  <InboxIcon color="disabled" />
                  <Typography variant="body2" color="text.secondary">
                    No shift applications match your filters.
                  </Typography>
                </Stack>
              ),
            }}
          />
        </Paper>
      )}

      <ApplicationReviewDrawer
        group={selectedGroup}
        onClose={() => setSelectedGroup(null)}
        onReviewed={loadApplications}
      />
    </Stack>
  )
}

export default Applications
